import React from 'react';
import styled from 'styled-components';
import { CollaboratorPresence } from './useAwareness';

const CursorLayer = styled.div`
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 25;
`;

const Cursor = styled.div<{ x: number; y: number; counterScale: number }>`
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(${({ x }) => x}px, ${({ y }) => y}px) scale(${({ counterScale }) => counterScale});
    transform-origin: top left;
    transition: transform 50ms linear;
    will-change: transform;
    animation: collab-cursor-fade-in 120ms ease-out;

    @keyframes collab-cursor-fade-in {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const Pointer = styled.svg<{ color: string }>`
    display: block;
    color: ${({ color }) => color};
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
`;

const Label = styled.div<{ color: string }>`
    margin-top: 2px;
    margin-left: 12px;
    background: ${({ color }) => color};
    color: white;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    white-space: nowrap;
    width: max-content;
`;

interface Props {
    peers: CollaboratorPresence[];
    /**
     * Current canvas zoom. Used to counter-scale the cursor visual so it stays
     * a constant pixel size regardless of zoom — the parent `PositioningContainer`
     * already applies `scale(zoomLevel)` to translate graph-space to screen-space,
     * so the cursor's `translate(x, y)` runs in raw graph coords.
     */
    zoomLevel: number;
}

/**
 * Overlay that renders remote collaborators' cursors.
 *
 * Mounts inside the drawing layer's `PositioningContainer`, which applies
 * `scale(zoomLevel)` to the whole subtree. Cursor positions are graph-space
 * Vectors as published by `useCursorPublisher`; the counter-scale on the
 * cursor visual cancels the parent zoom so pointers stay 16px tall.
 */
export const CollaboratorCursors: React.FC<Props> = ({ peers, zoomLevel }) => {
    const counterScale = zoomLevel === 0 ? 1 : 1 / zoomLevel;
    return (
        <CursorLayer>
            {peers
                .filter(peer => peer.cursor !== undefined)
                .map(peer => (
                    <Cursor key={peer.clientId} x={peer.cursor!.x} y={peer.cursor!.y} counterScale={counterScale}>
                        <Pointer color={peer.user.color} width="16" height="20" viewBox="0 0 16 20" fill="none">
                            <path d="M0 0L16 6L7 9L0 16V0Z" fill="currentColor" stroke="white" strokeWidth="1" />
                        </Pointer>
                        <Label color={peer.user.color}>{peer.user.displayName}</Label>
                    </Cursor>
                ))}
        </CursorLayer>
    );
};
